import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useUpdateEffect } from "react-use";
import { useResizeObserver } from "@activecollab/components";
import isEqual from "lodash/isEqual";
import moment from "moment";
import PropTypes from "prop-types";
import { getFirstWeekDay, getRawPersonalizedConfigOption } from "~/store/selectors/selector.configOptions";

const Context = React.createContext({
  selectedDate: moment().format("YYYY-MM-DD"),
  setSelectedDate: () => null,
  view: "week",
  setView: () => null,
  range: {
    from: moment().startOf("month").format("YYYY-MM-DD"),
    to: moment().endOf("month").format("YYYY-MM-DD"),
  },
  switchToDayView: false,
});

export const CalendarContext = ({ children }) => {
  const firstDayOfWeek = useSelector(getFirstWeekDay);
  const weekType = firstDayOfWeek === 0 ? "week" : "isoWeek";
  const [selectedDate, setSelectedDate] = useState(moment().format("YYYY-MM-DD"));
  const [range, setRange] = useState(() => {
    return {
      from: moment(selectedDate).startOf("month").startOf(weekType).format("YYYY-MM-DD"),
      to: moment(selectedDate).endOf("month").endOf(weekType).format("YYYY-MM-DD"),
    };
  });
  const calendarMode = useSelector((state) => getRawPersonalizedConfigOption(state, "display_mode_calendar"));
  const [view, setView] = useState(calendarMode);

  useUpdateEffect(() => {
    const result = {
      from: moment(selectedDate).startOf("month").startOf(weekType).format("YYYY-MM-DD"),
      to: moment(selectedDate).endOf("month").endOf(weekType).format("YYYY-MM-DD"),
    };

    setRange((prevState) => {
      if (isEqual(prevState, result)) {
        return prevState;
      }
      return result;
    });
  }, [selectedDate]);
  const prevented = useRef(false);
  const [switchToDayView, setSwitchToDayView] = useState(calendarMode);

  const ref = useRef(document.body);
  const dimensions = useResizeObserver(ref);

  useEffect(() => {
    if (dimensions?.width <= 768) {
      setSwitchToDayView(true);
    } else {
      setSwitchToDayView(false);
    }
  }, [dimensions]);

  return (
    <Context.Provider
      value={{
        selectedDate,
        setSelectedDate,
        view,
        setView,
        range,
        prevented,
        switchToDayView,
      }}
    >
      {children}
    </Context.Provider>
  );
};

CalendarContext.propTypes = {
  children: PropTypes.node.isRequired,
};

export const useCalendarContext = () => React.useContext(Context);
